import { useCallback, useMemo, useState } from 'react'
import { ReactDatePickerProps } from 'react-datepicker'
// @ts-expect-error: no type definition for direct ESM path
import DatePicker from 'react-datepicker/dist/react-datepicker.js';
import { parse } from 'date-fns';
import { FilterItemProps, inputClasses } from '../_common'
import clsx from 'clsx'
import { useGlobalState } from '../../../../../state/v2/store'
import addDays from 'date-fns/addDays'
import getWeekOfMonth from 'date-fns/getWeekOfMonth'
import startOfWeek from 'date-fns/startOfWeek'

export type DateRangePickerValue = [Date | null, Date | null]

interface DateRangeFilterItemProps
  extends FilterItemProps<DateRangePickerValue> {
    hasCurrentMonthData: boolean,  
  }

export function DateRangeFilter({
  handleChange,
  value,
  hasCurrentMonthData,
}: DateRangeFilterItemProps) {
  const [dateRange, setDateRange] = useState<DateRangePickerValue>(
    value || [null, null]
  )

  const selectedBrand = useGlobalState((b) => b.brand)
  const [startDate, endDate] = dateRange

  const onChange = useCallback(
    (dr: DateRangePickerValue) => {
      if (Array.isArray(dr)) {
        setDateRange(dr)
        handleChange?.(dr)
      }
    },
    [handleChange]
  )
  const { minDate, maxDate } = getMinMaxDates(hasCurrentMonthData);

  const props = useMemo<Partial<ReactDatePickerProps<true>>>(() => {
    const dataGranularity = selectedBrand?.[0]?.config?.dataGranularity;
  
    if (dataGranularity === 'WEEKLY') {
      return {
        formatWeekNumber: (date) => getWeekOfMonth(date),
        onSelect: (d) => {
          const firstDayOfWeek = startOfWeek(d, { weekStartsOn: 1 });
          let newRange = dateRange;
  
          if (startDate && startDate < firstDayOfWeek) {
            newRange = [startDate, addDays(firstDayOfWeek, 6)];
          } else {
            newRange = [firstDayOfWeek, addDays(firstDayOfWeek, 6)];
          }
  
          onChange(newRange);
        },
      };
    }
  
    return {
      showMonthYearPicker: true,
      placeholderText: 'MM-YYYY -> MM-YYYY',
      dateFormat: 'MM/yyyy',
      minDate,
      maxDate,
      filterDate: (date) =>
        date >= minDate && date <= maxDate
    };
  }, [dateRange, onChange, selectedBrand, startDate]);
  
  

  return (
    <DatePicker
      selectsRange
      startDate={startDate}
      endDate={endDate}
      maxDate={new Date()}
      isClearable
      className={clsx(inputClasses, 'mt-0 w-[275px] pr-6')}
      onChange={onChange}
      shouldCloseOnSelect={false}
      {...props}
    />
  )
}

export function getMinMaxDates(hasCurrentMonthData: boolean) {
  const today = new Date();

  const maxDate = hasCurrentMonthData ? new Date(today.getFullYear(), today.getMonth() + 1, 0) : new Date(today.getFullYear(), today.getMonth(), 0);
  const minDate = new Date(2023, 0, 1);
  return { minDate, maxDate };
}

export function getValidatedDateRange(
  startDateStr: string | null | undefined,
  endDateStr: string | null | undefined,
  hasCurrentMonthData: boolean
): [Date | null, Date | null] {
  const { minDate, maxDate } = getMinMaxDates(hasCurrentMonthData);

  if (!startDateStr || !endDateStr) return [null, null];

  const startDate = parse(startDateStr, 'yyyy-MM-dd', new Date());
  const endDate = parse(endDateStr, 'yyyy-MM-dd', new Date());

  if (startDate < minDate || startDate > maxDate || endDate < minDate || endDate > maxDate) {
    return [null, null];
  }

  return [startDate, endDate];
}
